import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { sceneFor, type Ornament as OrnamentKind } from '../data/imagine-scenes';
import { kraben } from '../card/kraben';

/**
 * How one blank of the idea roll is drawn: the piece for what the dice dealt
 * (`art`), or, when it is theirs, a present: `mine` is written and glowing,
 * `blank` is locked and still to write (the same present, unlit).
 */
export interface Piece { art?: string; mine?: boolean; blank?: boolean }

/** Where the three stand on the 320x180 card: you (a plain stand-in, until the photo), who is with you, and what you are doing. */
const YOU_X = 96;
const WHO_X = 142;
const GROUND = 140;

/**
 * The postcard (well/product/33 §9): a place, you standing in it, who is with
 * you and what you are doing, in plain shapes. Every rolled option has a piece
 * of its own (data/idea-shapes.ts, by `art`).
 * Anything they wrote is drawn as a present still wrapped (a lit door for a
 * place, a figure under a glowing sheet for a person, a gift box for what you
 * are doing): their own idea is the most exciting thing on the card, and the
 * crew's drawing is what opens it.
 */
export function IdeaPostcard({ who, where, doing, caption, label, onRollAll, rollAllLabel, picture, developing, fixedCaption }: {
  who: Piece;
  where: Piece;
  doing: Piece;
  caption: ReactNode;
  /** the whole postcard, read out */
  label: string;
  /** a die in the corner that rolls every blank that may roll */
  onRollAll?: () => void;
  rollAllLabel?: string;
  /** the crew's sketch of it, drawn in place of the plain shapes once it lands */
  picture?: string;
  /** the crew's sketch is on its way: the picture lies in the developer, still only colour */
  developing?: boolean;
  /** the caption keeps one height (two lines), and a long sentence gets smaller, so nothing under the card moves when the words change */
  fixedCaption?: boolean;
}) {
  const cap = useRef<HTMLParagraphElement>(null);
  useLayoutEffect(() => {
    const el = cap.current;
    if (!fixedCaption || !el) return;
    el.style.fontSize = '';
    let size = parseFloat(getComputedStyle(el).fontSize) || 22;
    while (el.scrollHeight > el.clientHeight + 1 && size > 13) { size -= 1; el.style.fontSize = `${size}px`; }
  }, [label, fixedCaption]);
  const sc = sceneFor(where.mine || where.blank ? undefined : where.art);
  const ink = sc.dark ? '#FFF3E0' : '#1B4FA8';
  const wrapped = (p: Piece) => !!(p.mine || p.blank);
  return (
    <div className={`postcard ${developing ? 'developing' : ''}`} role="img" aria-label={label}>
      <div className="pc-pic">
      {picture ? <img className="postcard-art postcard-picture" src={picture} alt="" /> : (
      <svg className="postcard-art" viewBox="0 0 320 180" preserveAspectRatio="xMidYMax slice" aria-hidden="true">
        <defs>
          <linearGradient id="pc-sky" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={sc.sky[0]} />
            <stop offset="1" stopColor={sc.sky[1]} />
          </linearGradient>
          <radialGradient id="pc-glow">
            <stop offset="0" stopColor="#FFE7A3" stopOpacity=".95" />
            <stop offset="1" stopColor="#FFE7A3" stopOpacity="0" />
          </radialGradient>
        </defs>
        <rect width="320" height="180" fill="url(#pc-sky)" />
        <Ornament kind={sc.ornament} lit={!!where.mine} />
        <rect y="130" width="320" height="50" fill={sc.ground} />
        {wrapped(doing) ? <WrappedDoing lit={!!doing.mine} /> : <Doing art={doing.art} />}
        <Person x={YOU_X} fill={ink} />
        {wrapped(who) ? <WrappedWho lit={!!who.mine} /> : <Who art={who.art} />}
      </svg>
      )}
      {/* the darkroom's developer (components/Reveal.tsx), washing over a picture not yet come up */}
      {developing && (
        <div className="pc-wash" aria-hidden="true">
          <img className="wash" src="/art/reveal/developing.jpg" alt="" draggable={false} />
          <img className="wash w2" src="/art/reveal/developing.jpg" alt="" draggable={false} />
          <span className="sweep" />
        </div>
      )}
      </div>
      <p ref={cap} className={`postcard-caption ${fixedCaption ? 'fixed' : ''}`} aria-hidden="true"><span>{caption}</span></p>
      {onRollAll && (
        <button type="button" className="pc-roll-all" onClick={onRollAll} aria-label={rollAllLabel}>
          <Die />
        </button>
      )}
    </div>
  );
}

/** A die face, for the roll buttons. */
export function Die({ size = 22 }: { size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
      <rect x="2.5" y="2.5" width="19" height="19" rx="5" fill="#FFFFFF" stroke="currentColor" strokeWidth="2" />
      <g fill="currentColor"><circle cx="8" cy="8" r="1.8" /><circle cx="16" cy="16" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="16" cy="8" r="1.8" /><circle cx="8" cy="16" r="1.8" /></g>
    </svg>
  );
}

/** Somebody standing, feet on the ground at x: a head, a body, two legs. */
function Person({ x, fill, s = 1 }: { x: number; fill: string; s?: number }) {
  return (
    <g fill={fill} transform={s === 1 ? undefined : `translate(${x} ${GROUND}) scale(${s}) translate(${-x} ${-GROUND})`}>
      <circle cx={x} cy="97" r="9.5" />
      <rect x={x - 10} y="108" width="20" height="26" rx="8" />
      <rect x={x - 8} y="128" width="6.5" height="12" rx="3" />
      <rect x={x + 1.5} y="128" width="6.5" height="12" rx="3" />
    </g>
  );
}

function Who({ art }: { art?: string }) {
  const x = WHO_X;
  switch (art) {
    case 'granny':
      return (
        <g>
          <Person x={x} fill="#8E5BA8" />
          <circle cx={x} cy="85" r="5.5" fill="#D8D2E3" />
          <g fill="none" stroke="#F4EEF8" strokeWidth="1.4"><circle cx={x - 3.6} cy="97" r="2.8" /><circle cx={x + 3.6} cy="97" r="2.8" /></g>
        </g>
      );
    case 'kid':
      return (
        <g>
          <Person x={x} fill="#E2674F" s={0.78} />
          <path d={`M${x - 7.5} ${GROUND - 0.78 * 43} a7.5 7.5 0 0 1 15 0 z`} fill="#2E7D4F" />
          <rect x={x - 1} y={GROUND - 0.78 * 43 - 1} width="11" height="2.6" rx="1.3" fill="#2E7D4F" />
        </g>
      );
    case 'dog':
      return (
        <g fill="#B07A47">
          <ellipse cx={x} cy="126" rx="14" ry="8.5" />
          <circle cx={x + 13} cy="115" r="7.5" />
          <ellipse cx={x + 10} cy="111" rx="3" ry="5.5" fill="#7E5230" transform={`rotate(-20 ${x + 10} 111)`} />
          <circle cx={x + 16} cy="114" r="1.3" fill="#1B2440" />
          <rect x={x - 11} y="130" width="4.5" height="10" rx="2" /><rect x={x - 3} y="130" width="4.5" height="10" rx="2" />
          <rect x={x + 4} y="130" width="4.5" height="10" rx="2" /><rect x={x + 9} y="130" width="4.5" height="10" rx="2" />
          <path d={`M${x - 13} 122 q-8 -6 -6 -14`} stroke="#B07A47" strokeWidth="3.5" fill="none" strokeLinecap="round" />
        </g>
      );
    case 'robot':
      return (
        <g>
          <line x1={x} y1="82" x2={x} y2="90" stroke="#8FA3B8" strokeWidth="2" />
          <circle cx={x} cy="81" r="2.6" fill="#F2705B" />
          <rect x={x - 9} y="90" width="18" height="15" rx="3.5" fill="#B7C6D6" />
          <circle cx={x - 3.8} cy="97.5" r="2" fill="#1B2440" /><circle cx={x + 3.8} cy="97.5" r="2" fill="#1B2440" />
          <rect x={x - 11} y="107" width="22" height="22" rx="4" fill="#8FA3B8" />
          <rect x={x - 4} y="113" width="8" height="5" rx="1.5" fill="#FFD36B" />
          <rect x={x - 8} y="129" width="6" height="11" rx="2" fill="#6F8399" /><rect x={x + 2} y="129" width="6" height="11" rx="2" fill="#6F8399" />
        </g>
      );
    case 'ghost':
      return (
        <g>
          <path d={`M${x - 12} 136 V102 a12 12 0 0 1 24 0 V136 l-4 -5 -4 5 -4 -5 -4 5 -4 -5 z`} fill="#FFFFFF" opacity=".95" />
          <circle cx={x - 4} cy="103" r="2.2" fill="#1B2440" /><circle cx={x + 4} cy="103" r="2.2" fill="#1B2440" />
          <ellipse cx={x} cy="110" rx="2.2" ry="3" fill="#1B2440" />
        </g>
      );
    case 'kraben':
      return <g transform={`translate(${x - 27} 86)`} dangerouslySetInnerHTML={{ __html: kraben(54) }} />;
    case 'pirate':
      return (
        <g>
          <Person x={x} fill="#C0504D" />
          <polygon points={`${x - 13},91 ${x + 13},91 ${x},80`} fill="#1E1E2E" />
          <rect x={x - 13} y="89" width="26" height="3.5" rx="1.5" fill="#1E1E2E" />
          <circle cx={x + 3.5} cy="97" r="2.6" fill="#1E1E2E" />
          <line x1={x - 6} y1="93" x2={x + 8} y2="99" stroke="#1E1E2E" strokeWidth="1.2" />
        </g>
      );
    case 'friend':
    default:
      return <Person x={x} fill="#2F8A57" />;
  }
}

/** Someone they named: a figure under a sheet tied with a ribbon, glowing once it is written. */
function WrappedWho({ lit }: { lit: boolean }) {
  const x = WHO_X;
  // a head under the sheet, shoulders, and the sheet falling to the ground
  const sheet = `M${x - 15} 140 L${x - 13} 113 Q${x - 13} 105 ${x - 7} 104 A10 10 0 1 1 ${x + 7} 104 Q${x + 13} 105 ${x + 13} 113 L${x + 15} 140 Z`;
  return (
    <g className={lit ? 'pc-lit' : 'pc-unlit'}>
      {lit && <circle className="pc-glow" cx={x} cy="112" r="36" fill="url(#pc-glow)" />}
      <path d={sheet} fill={lit ? '#F2A1B5' : 'rgba(255,255,255,.08)'} stroke={lit ? '#D9677F' : '#B9A6EE'} strokeWidth="2" strokeDasharray={lit ? undefined : '4 3'} />
      {lit && (
        <g>
          <path d={`M${x - 8} 112 q-3 14 -2 27 M${x + 8} 112 q3 14 2 27`} stroke="#D9677F" strokeWidth="1.3" fill="none" opacity=".55" />
          <rect x={x - 13.5} y="110" width="27" height="4" rx="2" fill="#FFD36B" />
          <ellipse cx={x - 5} cy="109" rx="5" ry="3.2" fill="#FFD36B" transform={`rotate(-20 ${x - 5} 109)`} />
          <ellipse cx={x + 5} cy="109" rx="5" ry="3.2" fill="#FFD36B" transform={`rotate(20 ${x + 5} 109)`} />
          <circle cx={x} cy="110.5" r="2.2" fill="#E8B23A" />
        </g>
      )}
      <text x={x} y={lit ? '133' : '128'} textAnchor="middle" fontSize={lit ? '15' : '18'} fontWeight="900" fill={lit ? '#FFFFFF' : '#B9A6EE'}>?</text>
      {lit && <Sparkles points={[[x - 20, 92], [x + 19, 100], [x + 14, 82]]} />}
    </g>
  );
}

function Doing({ art }: { art?: string }) {
  switch (art) {
    case 'crow':
      return (
        <g>
          <rect x="236" y="118" width="4.5" height="22" rx="1.5" fill="#8A5A35" />
          <ellipse cx="238" cy="113" rx="10" ry="6.5" fill="#1F2333" />
          <circle cx="247" cy="106" r="5.5" fill="#1F2333" />
          <polygon points="252,105 260,107 252,109" fill="#F5A33B" />
          <circle cx="248.5" cy="104.5" r="1.2" fill="#FFFFFF" />
          <rect x="252" y="80" width="30" height="16" rx="7" fill="#FFFFFF" />
          <polygon points="257,95 254,102 263,95" fill="#FFFFFF" />
          <g fill="#1B2440"><circle cx="260" cy="88" r="1.8" /><circle cx="267" cy="88" r="1.8" /><circle cx="274" cy="88" r="1.8" /></g>
        </g>
      );
    case 'kite':
      return (
        <g>
          <path d="M106 112 Q190 110 246 60" stroke="#1B2440" strokeOpacity=".45" strokeWidth="1.1" fill="none" />
          <polygon points="252,20 270,40 252,64 234,40" fill="#F2705B" />
          <path d="M252 20 V64 M234 40 H270" stroke="#FFD36B" strokeWidth="2" />
          <path d="M252 64 l-5 7 5 4 -5 7 5 4" stroke="#FFD36B" strokeWidth="2" fill="none" />
        </g>
      );
    case 'guitar':
      return (
        <g transform="rotate(-14 232 120)">
          <rect x="229" y="70" width="6" height="36" rx="2" fill="#8A5A35" />
          <rect x="227" y="64" width="10" height="9" rx="2" fill="#6B4226" />
          <circle cx="232" cy="110" r="9.5" fill="#E8913A" />
          <circle cx="232" cy="125" r="13" fill="#E8913A" />
          <circle cx="232" cy="115" r="3.2" fill="#5A3518" />
        </g>
      );
    case 'cake':
      return (
        <g>
          <rect x="208" y="112" width="64" height="28" rx="4" fill="#F5A6B8" />
          <rect x="217" y="94" width="46" height="20" rx="4" fill="#FFF3D6" />
          <rect x="226" y="79" width="28" height="17" rx="4" fill="#F5A6B8" />
          <path d="M208 120 q8 6 16 0 t16 0 t16 0 t16 0" stroke="#FFFFFF" strokeWidth="2.5" fill="none" />
          <rect x="238" y="66" width="4" height="13" rx="1.5" fill="#FFD36B" />
          <ellipse cx="240" cy="62" rx="3" ry="5" fill="#F5A33B" />
        </g>
      );
    case 'chest':
      return (
        <g>
          <ellipse cx="238" cy="140" rx="38" ry="9" fill="#8A5A35" />
          <circle cx="238" cy="108" r="22" fill="url(#pc-glow)" />
          <rect x="220" y="114" width="36" height="22" rx="3" fill="#9C5B2E" />
          <polygon points="220,114 256,114 252,100 224,100" fill="#B56D3A" />
          <rect x="220" y="121" width="36" height="4" fill="#FFC53D" />
          <g fill="#FFC53D"><circle cx="230" cy="112" r="3" /><circle cx="238" cy="110" r="3" /><circle cx="246" cy="112" r="3" /></g>
          <line x1="276" y1="140" x2="286" y2="104" stroke="#8A5A35" strokeWidth="3" strokeLinecap="round" />
          <path d="M270 140 h12 l-2 -10 h-8 z" fill="#9FB3C8" />
        </g>
      );
    case 'map':
      return (
        <g>
          <line x1="226" y1="140" x2="232" y2="120" stroke="#8A5A35" strokeWidth="3" /><line x1="266" y1="140" x2="260" y2="120" stroke="#8A5A35" strokeWidth="3" />
          <g transform="rotate(-4 246 104)">
            <rect x="214" y="84" width="64" height="42" rx="3" fill="#F3E2B3" />
            <path d="M222 118 q14 -22 26 -8 t22 -18" stroke="#F2705B" strokeWidth="2" strokeDasharray="4 3" fill="none" />
            <path d="M266 88 l8 8 M274 88 l-8 8" stroke="#C0392B" strokeWidth="2.6" strokeLinecap="round" />
          </g>
        </g>
      );
    case 'snails':
      return (
        <g>
          <ellipse cx="214" cy="137" rx="12" ry="3.5" fill="#C7D98F" />
          <circle cx="214" cy="129" r="8.5" fill="#E8913A" /><circle cx="214" cy="129" r="3.5" fill="none" stroke="#B5611E" strokeWidth="1.6" />
          <ellipse cx="254" cy="137" rx="12" ry="3.5" fill="#C7D98F" />
          <circle cx="254" cy="129" r="8.5" fill="#8E5BA8" /><circle cx="254" cy="129" r="3.5" fill="none" stroke="#5E3577" strokeWidth="1.6" />
          <line x1="288" y1="140" x2="288" y2="98" stroke="#1B2440" strokeWidth="2" />
          <g><rect x="288" y="98" width="18" height="12" fill="#FFFFFF" /><rect x="288" y="98" width="6" height="6" fill="#1B2440" /><rect x="300" y="98" width="6" height="6" fill="#1B2440" /><rect x="294" y="104" width="6" height="6" fill="#1B2440" /></g>
        </g>
      );
    case 'rocket':
      return (
        <g>
          <g fill="#FFFFFF" opacity=".8">{[[214, 128, 3], [222, 116, 3.5], [230, 104, 4], [238, 92, 4.5]].map(([cx, cy, r]) => <circle key={cx} cx={cx} cy={cy} r={r} />)}</g>
          <g transform="rotate(38 256 62)">
            <path d="M256 30 C266 42 266 70 262 84 H250 C246 70 246 42 256 30 Z" fill="#FFFFFF" stroke="#9FB3C8" strokeWidth="1.2" />
            <polygon points="250,74 242,88 250,84" fill="#F2705B" /><polygon points="262,74 270,88 262,84" fill="#F2705B" />
            <circle cx="256" cy="54" r="4" fill="#7DB6E8" />
          </g>
        </g>
      );
    case 'typewriter':
      return (
        <g>
          <rect x="222" y="126" width="40" height="14" rx="2" fill="#8A5A35" />
          <rect x="226" y="108" width="32" height="20" rx="4" fill="#2F8A57" />
          <rect x="232" y="94" width="20" height="16" fill="#FFFFFF" />
          <g fill="#1B4FA8" opacity=".5"><rect x="235" y="98" width="14" height="1.6" /><rect x="235" y="102" width="10" height="1.6" /></g>
          <g fill="#FFF3D6">{[230, 236, 242, 248].map((x) => <circle key={x} cx={x + 2} cy="121" r="2" />)}</g>
        </g>
      );
    case 'book':
      return (
        <g>
          <path d="M214 120 L240 114 L266 120 L266 140 L240 134 L214 140 Z" fill="#FFFFFF" stroke="#C9553F" strokeWidth="2" />
          <line x1="240" y1="114" x2="240" y2="134" stroke="#C9553F" strokeWidth="2" />
          <g stroke="#1B4FA8" strokeOpacity=".45" strokeWidth="1.4"><line x1="220" y1="124" x2="235" y2="121" /><line x1="220" y1="129" x2="235" y2="126" /><line x1="245" y1="121" x2="260" y2="124" /><line x1="245" y1="126" x2="260" y2="129" /></g>
        </g>
      );
    case 'easel':
      return (
        <g>
          <line x1="230" y1="140" x2="240" y2="70" stroke="#8A5A35" strokeWidth="3" /><line x1="262" y1="140" x2="252" y2="70" stroke="#8A5A35" strokeWidth="3" />
          <rect x="220" y="76" width="52" height="40" rx="2" fill="#FFFFFF" stroke="#8A5A35" strokeWidth="2" />
          <circle cx="236" cy="92" r="7" fill="#FFD36B" /><path d="M222 112 Q240 96 270 110 V114 H222 Z" fill="#7DB6E8" /><circle cx="256" cy="98" r="5" fill="#F2705B" />
        </g>
      );
    case 'camera':
      return (
        <g>
          <line x1="242" y1="112" x2="230" y2="140" stroke="#3E4A5E" strokeWidth="2.5" /><line x1="242" y1="112" x2="254" y2="140" stroke="#3E4A5E" strokeWidth="2.5" /><line x1="242" y1="112" x2="242" y2="140" stroke="#3E4A5E" strokeWidth="2.5" />
          <rect x="226" y="96" width="32" height="18" rx="3" fill="#3E4A5E" />
          <circle cx="234" cy="90" r="7" fill="#1E1E2E" /><circle cx="250" cy="90" r="7" fill="#1E1E2E" />
          <polygon points="258,100 270,94 270,116 258,110" fill="#3E4A5E" />
          <circle cx="232" cy="105" r="2" fill="#F2705B" />
        </g>
      );
    case 'mic':
      return (
        <g>
          <line x1="244" y1="104" x2="244" y2="138" stroke="#3E4A5E" strokeWidth="3" />
          <ellipse cx="244" cy="140" rx="12" ry="3" fill="#3E4A5E" />
          <rect x="237" y="84" width="14" height="22" rx="7" fill="#9FB3C8" />
          <g stroke="#6F8399" strokeWidth="1"><line x1="239" y1="90" x2="249" y2="90" /><line x1="239" y1="95" x2="249" y2="95" /></g>
          <path d="M228 86 q-6 8 0 16 M260 86 q6 8 0 16" stroke="#F2705B" strokeWidth="2" fill="none" strokeLinecap="round" />
        </g>
      );
    case 'phone':
      return (
        <g>
          <line x1="244" y1="110" x2="236" y2="140" stroke="#3E4A5E" strokeWidth="2.5" /><line x1="244" y1="110" x2="252" y2="140" stroke="#3E4A5E" strokeWidth="2.5" />
          <circle cx="244" cy="92" r="20" fill="none" stroke="#FFF3C4" strokeWidth="5" opacity=".9" />
          <rect x="236" y="78" width="16" height="28" rx="3" fill="#1E1E2E" />
          <rect x="238" y="81" width="12" height="20" rx="1.5" fill="#7DB6E8" />
          <circle cx="262" cy="80" r="3" fill="#F2705B" />
        </g>
      );
    case 'stall':
      return (
        <g>
          <rect x="212" y="104" width="62" height="36" fill="#FFF3D6" />
          <rect x="216" y="112" width="54" height="14" fill="#FFFFFF" />
          <g fill="#FFD36B">{[222, 234, 246, 258].map((x) => <circle key={x} cx={x + 2} cy="119" r="4" />)}</g>
          <path d="M208 104 h70 l-6 -16 h-58 z" fill="#F2705B" />
          <g fill="#FFFFFF">{[214, 228, 242, 256].map((x) => <path key={x} d={`M${x} 88 h7 l3 16 h-7 z`} />)}</g>
          <line x1="214" y1="104" x2="214" y2="140" stroke="#8A5A35" strokeWidth="3" /><line x1="272" y1="104" x2="272" y2="140" stroke="#8A5A35" strokeWidth="3" />
        </g>
      );
    case 'workbench':
      return (
        <g>
          <rect x="210" y="118" width="64" height="6" fill="#8A5A35" /><rect x="214" y="124" width="5" height="16" fill="#8A5A35" /><rect x="265" y="124" width="5" height="16" fill="#8A5A35" />
          <rect x="232" y="98" width="18" height="20" rx="3" fill="#B7C6D6" />
          <rect x="235" y="86" width="12" height="11" rx="2" fill="#8FA3B8" />
          <circle cx="239" cy="91" r="1.6" fill="#1B2440" /><circle cx="244" cy="91" r="1.6" fill="#1B2440" />
          <path d="M256 116 l10 -10 M262 104 a4 4 0 1 1 5 5" stroke="#3E4A5E" strokeWidth="2.5" fill="none" strokeLinecap="round" />
        </g>
      );
    default:
      return null;
  }
}

/** Something they are doing, in their own words: a present, glowing once it is written. */
function WrappedDoing({ lit }: { lit: boolean }) {
  return (
    <g className={lit ? 'pc-lit' : 'pc-unlit'}>
      {lit && <circle className="pc-glow" cx="240" cy="114" r="40" fill="url(#pc-glow)" />}
      {lit ? (
        <g>
          <rect x="216" y="106" width="48" height="34" rx="3" fill="#F2705B" />
          <rect x="212" y="98" width="56" height="11" rx="3" fill="#D9573F" />
          <rect x="236" y="98" width="8" height="42" fill="#FFD36B" />
          <ellipse cx="231" cy="94" rx="9" ry="5.5" fill="#FFD36B" transform="rotate(-18 231 94)" />
          <ellipse cx="249" cy="94" rx="9" ry="5.5" fill="#FFD36B" transform="rotate(18 249 94)" />
          <circle cx="240" cy="96" r="3.5" fill="#E8B23A" />
          <Sparkles points={[[206, 96], [276, 104], [262, 80]]} />
        </g>
      ) : (
        <g fill="none" stroke="#B9A6EE" strokeWidth="2" strokeDasharray="4 3">
          <rect x="216" y="106" width="48" height="34" rx="3" />
          <rect x="212" y="98" width="56" height="11" rx="3" />
          <text x="240" y="130" textAnchor="middle" fontSize="18" fontWeight="900" fill="#B9A6EE" stroke="none">?</text>
        </g>
      )}
    </g>
  );
}

function Sparkles({ points }: { points: [number, number][] }) {
  return (
    <g className="pc-sparkle" fill="#FFF3C4">
      {points.map(([x, y], i) => <path key={i} d={`M${x} ${y - 5} L${x + 1.5} ${y - 1.5} L${x + 5} ${y} L${x + 1.5} ${y + 1.5} L${x} ${y + 5} L${x - 1.5} ${y + 1.5} L${x - 5} ${y} L${x - 1.5} ${y - 1.5} Z`} />)}
    </g>
  );
}

function Ornament({ kind, lit }: { kind: OrnamentKind; lit: boolean }) {
  switch (kind) {
    case 'door':
      // a place of their own: a door at the back of a dark stage, lit from behind once they have written it
      return (
        <g className={lit ? 'pc-lit' : 'pc-unlit'}>
          <g fill="#FFFFFF" opacity=".55">{[[30, 30], [80, 50], [120, 22], [250, 30], [290, 60], [60, 90], [282, 100]].map(([x, y], i) => <circle key={i} cx={x} cy={y} r={i % 2 ? 1.3 : 2} />)}</g>
          {lit && <polygon className="pc-glow" points="176,132 204,132 300,180 80,180" fill="#FFE7A3" opacity=".35" />}
          {lit && <circle className="pc-glow" cx="190" cy="92" r="44" fill="url(#pc-glow)" />}
          <path d="M172 132 V74 a18 18 0 0 1 36 0 V132 Z" fill={lit ? '#FFE7A3' : '#3A2A70'} stroke={lit ? '#FFF3C4' : '#B9A6EE'} strokeWidth="2" strokeDasharray={lit ? undefined : '4 3'} />
          {lit ? <Sparkles points={[[160, 70], [222, 84], [196, 48]]} /> : <text x="190" y="110" textAnchor="middle" fontSize="20" fontWeight="900" fill="#B9A6EE">?</text>}
        </g>
      );
    case 'moon':
      return (
        <g>
          <circle cx="262" cy="46" r="26" fill="#F3EBC8" />
          <circle cx="252" cy="40" r="5" fill="#E4DAB0" />
          <circle cx="271" cy="56" r="3.5" fill="#E4DAB0" />
          <g fill="#FFF3C4"><circle cx="40" cy="30" r="2" /><circle cx="100" cy="20" r="1.5" /><circle cx="160" cy="48" r="2.2" /><circle cx="60" cy="80" r="1.5" /><circle cx="200" cy="90" r="1.6" /></g>
        </g>
      );
    case 'stars':
      return (
        <g fill="#FFF3C4">
          {[[30, 30], [80, 18], [140, 40], [200, 22], [250, 50], [290, 20], [60, 70], [180, 70], [300, 80], [120, 100], [230, 105]].map(([x, y], i) => <circle key={i} cx={x} cy={y} r={i % 3 ? 1.6 : 2.6} />)}
        </g>
      );
    case 'clouds':
      return (
        <g fill="#FFFFFF" opacity=".95">
          <Cloud x={50} y={66} s={1} /><Cloud x={215} y={36} s={1.25} /><Cloud x={150} y={108} s={.8} />
          <g fill="#E6EEF9"><rect x="250" y="62" width="40" height="40" /><rect x="244" y="54" width="12" height="16" /><rect x="284" y="54" width="12" height="16" /></g>
        </g>
      );
    case 'peaks':
      return (
        <g>
          <polygon points="0,130 70,50 140,130" fill="#DCE9F5" /><polygon points="70,50 50,74 90,74" fill="#FFFFFF" />
          <polygon points="120,130 200,30 280,130" fill="#C9DCF0" /><polygon points="200,30 176,62 224,62" fill="#FFFFFF" />
          <polygon points="240,130 300,70 360,130" fill="#DCE9F5" />
        </g>
      );
    case 'school':
      return (
        <g>
          <rect x="60" y="58" width="200" height="74" fill="#C9553F" />
          <polygon points="50,60 160,18 270,60" fill="#8E3A2B" />
          {[84, 126, 174, 216].map((x) => <rect key={x} x={x} y="80" width="22" height="22" rx="2" fill="#FFF2C9" />)}
          <circle cx="160" cy="44" r="9" fill="#FFF8E6" /><path d="M160 44V38M160 44H164" stroke="#8E3A2B" strokeWidth="1.5" strokeLinecap="round" />
        </g>
      );
    case 'dunes':
      return (
        <g>
          <circle cx="262" cy="40" r="20" fill="#FFF2B0" />
          <ellipse cx="80" cy="140" rx="140" ry="30" fill="#D9A15A" />
          <ellipse cx="270" cy="150" rx="150" ry="34" fill="#F0C27D" />
          <ellipse cx="40" cy="128" rx="26" ry="6" fill="#5FB3D9" />
          <path d="M40 124 C40 104 34 96 30 92" stroke="#7A5230" strokeWidth="3" fill="none" strokeLinecap="round" />
          <ellipse cx="30" cy="90" rx="12" ry="5" fill="#4E9E4B" />
        </g>
      );
    case 'dome':
      return (
        <g>
          <g fill="#FFFFFF">{[[40, 40], [90, 70], [130, 30], [180, 60], [230, 36], [270, 76], [60, 110], [200, 100], [290, 110], [150, 85]].map(([x, y], i) => <circle key={i} cx={x} cy={y} r={2.4} />)}</g>
          <path d="M20 132 A140 140 0 0 1 300 132" fill="none" stroke="#FFFFFF" strokeOpacity=".9" strokeWidth="4" />
          <path d="M20 132 A140 140 0 0 1 300 132" fill="#FFFFFF" fillOpacity=".12" />
          <g fill="#C9553F"><rect x="40" y="110" width="22" height="20" /><polygon points="36,112 51,98 66,112" fill="#8E3A2B" /><rect x="280" y="112" width="18" height="18" /><polygon points="276,114 289,102 302,114" fill="#8E3A2B" /></g>
        </g>
      );
    case 'sun':
      return (
        <g>
          <circle cx="160" cy="132" r="58" fill="#FFD36B" opacity=".3" />
          <circle cx="160" cy="132" r="40" fill="#FFD36B" />
          <path d="M0 136 q20 -6 40 0 t40 0 t40 0 t40 0 t40 0 t40 0 t40 0 t40 0" stroke="#FFFFFF" strokeOpacity=".7" strokeWidth="2" fill="none" />
        </g>
      );
    case 'rain':
      return (
        <g>
          <g stroke="#E3EAF4" strokeWidth="2" strokeLinecap="round" opacity=".8">
            {[10, 50, 90, 130, 170, 210, 250, 290].map((x, i) => <line key={x} x1={x + (i % 2) * 14} y1={12 + (i % 3) * 22} x2={x + (i % 2) * 14 - 8} y2={40 + (i % 3) * 22} />)}
            {[30, 70, 110, 150, 190, 230, 270, 310].map((x, i) => <line key={x} x1={x} y1={70 + (i % 2) * 20} x2={x - 8} y2={98 + (i % 2) * 20} />)}
          </g>
          <rect x="40" y="70" width="4" height="62" fill="#3E4A5E" /><rect x="22" y="66" width="44" height="6" rx="2" fill="#3E4A5E" />
          <rect x="34" y="78" width="16" height="12" rx="2" fill="#F2705B" />
        </g>
      );
    case 'trees':
      return (
        <g>
          {[[16, .9], [58, 1.15], [100, .75], [205, 1.25], [252, .9], [300, 1.05]].map(([x, s], i) => (
            <polygon key={i} points={`${x},${132} ${x + 22 * s},${132} ${x + 11 * s},${132 - 70 * s}`} fill={i % 2 ? '#3E7A3D' : '#2F6530'} />
          ))}
        </g>
      );
    case 'island':
      return (
        <g>
          <circle cx="270" cy="40" r="20" fill="#FFF2B0" />
          <ellipse cx="60" cy="134" rx="78" ry="14" fill="#E9D59A" />
          <rect x="22" y="64" width="16" height="66" fill="#FFFFFF" /><rect x="22" y="80" width="16" height="10" fill="#F2705B" /><rect x="22" y="104" width="16" height="10" fill="#F2705B" />
          <rect x="18" y="54" width="24" height="12" rx="2" fill="#FFD36B" /><polygon points="16,56 30,42 44,56" fill="#C0392B" />
        </g>
      );
    case 'fish':
      return (
        <g>
          <g fill="#FFFFFF" opacity=".5">{[[40, 50], [60, 30], [220, 70], [240, 40], [290, 100]].map(([x, y], i) => <circle key={i} cx={x} cy={y} r={i % 2 ? 3 : 4.5} />)}</g>
          <g fill="#FFC53D"><ellipse cx="80" cy="62" rx="14" ry="8" /><polygon points="94,62 106,54 106,70" /></g>
          <g fill="#F2705B"><ellipse cx="250" cy="92" rx="12" ry="7" /><polygon points="238,92 226,85 226,99" /></g>
        </g>
      );
  }
}

function Cloud({ x, y, s }: { x: number; y: number; s: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${s})`}>
      <ellipse cx="0" cy="0" rx="26" ry="12" /><circle cx="-10" cy="-8" r="12" /><circle cx="8" cy="-10" r="14" />
    </g>
  );
}
